import { t } from 'i18next';
import {
  Control,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

import { Button, Confirmation, DateInput, Field, Modal, Visible } from 'coreOld/components';
import { AlignText, ButtonType, InputType, Size } from 'coreOld/enums';
import { HeaderItem, HeaderWrapper } from 'partsOld/elements';
import { ICost, ICostItem, ICostForm } from 'partsOld/types';

import {
  BottomBtnWrapper,
  BtnWrapper,
  ButtonWrapper,
  FieldsWrapper,
  Flex,
  List,
  ListWrapper,
  RemoveBtn,
  Wrapper,
} from './costFormStyle';
import CostItem from './CostItem';

const getConfirmationModalLabels = (isRemoveMode: boolean) => {
  if (isRemoveMode) {
    return {
      confirmationTitle: t('labelRemoveActivePartCost'),
      confirmBtnLabel: t('buttonLabelRemove'),
      message: t('messageRemoveActivePartCost'),
    };
  }

  return {
    confirmationTitle: t('labelEndActivePartCost'),
    confirmBtnLabel: t('buttonLabelEnd'),
    message: t('messageEndActivePartCost'),
  };
};

interface IProps {
  control: Control<ICostForm, object>;
  handleSubmit: UseFormHandleSubmit<ICostForm>;
  getValues: UseFormGetValues<ICostForm>;
  startDate: Date;
  endDate: Date;
  isFormOpen: boolean;
  costItems?: ICostItem[];
  isCostSelected: boolean;
  isActiveCostOverlapping?: boolean;
  removedItem: ICost;
  addCost?: (skipValidation: boolean) => Promise<void>;
  removeCost?: (index: number, item?: ICostItem) => void;
  onCancel: () => void;
  onCancelBtnClick: () => void;
  onRemove: () => void;
  onCancelRemove: () => void;
  onChangeDate: (date, field: string) => void;
  onSubmit: (skipActiveCheck?: boolean) => (data: any) => Promise<void>;
  onCloseModal: () => void;
  setValue?: UseFormSetValue<ICostForm>;
  watch: UseFormWatch<ICostForm>;
}

const headerItems = [
  {
    label: 'Min Qty',
    gridItem: 3.5,
  },
  {
    label: 'Max Qty',
    gridItem: 3.5,
  },
  {
    label: 'Cost',
    gridItem: 3.5,
  },
  {
    label: 'Per',
    gridItem: 1,
    style: {
      textAlign: AlignText.LEFT,
    },
  },
];

const CostFormView = ({
  startDate,
  endDate,
  onChangeDate,
  addCost,
  removeCost,
  costItems,
  control,
  isFormOpen,
  onCloseModal,
  onSubmit,
  handleSubmit,
  getValues,
  isCostSelected,
  isActiveCostOverlapping,
  onCancel,
  onRemove,
  onCancelRemove,
  removedItem,
  onCancelBtnClick,
  setValue,
  watch,
}: IProps) => {
  const title = isCostSelected ? t('labelUpdatePartCost') : t('labelCreatePartCost');

  const isRemoveModal = Boolean(removedItem);
  const labels = getConfirmationModalLabels(isRemoveModal);

  return (
    <>
      <Modal title={title} isOpen={isFormOpen} onClose={onCloseModal} size={Size.LG}>
        <Wrapper>
          <FieldsWrapper>
            <Flex>
              <DateInput
                label={t('labelStartDate')}
                selected={startDate}
                startDate={startDate}
                minDate={new Date()}
                onChange={(date) => onChangeDate(date, 'startDate')}
                showTimeInput={true}
                size={Size.LG}
                showBorder={true}
                hasBorder={false}
                required
              />
            </Flex>

            <Flex>
              <DateInput
                label={t('labelEndDate')}
                selected={endDate}
                endDate={endDate}
                startDate={startDate}
                minDate={startDate}
                onChange={(date) => onChangeDate(date, 'endDate')}
                showTimeInput={true}
                size={Size.LG}
                showBorder={true}
                hasBorder={false}
              />
            </Flex>

            <Flex>
              <Field
                type={InputType.TEXTFIELD}
                name="uom"
                label={t('labelCostUom')}
                required
                inputProps={{ control, size: Size.LG, hasBorder: false }}
              />
            </Flex>
          </FieldsWrapper>

          <ListWrapper>
            <List>
              <HeaderWrapper borderBottom container spacing={1}>
                {headerItems.map((item) => (
                  <HeaderItem
                    key={item.label}
                    style={{ ...item.style }}
                    item
                    xs={item.gridItem as any}
                  >
                    {item.label}
                  </HeaderItem>
                ))}
              </HeaderWrapper>

              {costItems.map((item, index) => {
                return (
                  <CostItem
                    key={item.fieldId}
                    control={control}
                    index={index}
                    getValues={getValues}
                    removeCost={() => removeCost(index, item)}
                    setValue={setValue}
                    watch={watch}
                  />
                );
              })}
            </List>
          </ListWrapper>

          <BtnWrapper>
            <ButtonWrapper>
              <Button onClick={() => addCost(false)} size={Size.SM}>
                {t('buttonLabelAddQuantity')}
              </Button>
            </ButtonWrapper>

            <Button onClick={handleSubmit(onSubmit(false))}>{title}</Button>
          </BtnWrapper>

          <Visible when={isCostSelected}>
            <BottomBtnWrapper>
              <RemoveBtn>
                <Button onClick={onCancelBtnClick} btnType={ButtonType.DANGER}>
                  {t('buttonLabelRemovePartCost')}
                </Button>
              </RemoveBtn>
            </BottomBtnWrapper>
          </Visible>
        </Wrapper>
      </Modal>

      <Visible when={isActiveCostOverlapping || isRemoveModal}>
        <Confirmation
          isOpen={isActiveCostOverlapping || isRemoveModal}
          onConfirm={isRemoveModal ? onRemove : handleSubmit(onSubmit(true))}
          onCancel={isRemoveModal ? onCancelRemove : onCancel}
          title={labels.confirmationTitle}
          confirmBtnLabel={labels.confirmBtnLabel}
          message={labels.message}
          confirmBtnType={ButtonType.DANGER}
          size={Size.MD}
        />
      </Visible>
    </>
  );
};

export default CostFormView;
